import React, { useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register the thresholdBackgroundPlugin locally
const thresholdBackgroundPlugin = {
    id: 'thresholdBackground',
    beforeDraw: (chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(
            chartArea.left, 
            chartArea.top, 
            chartArea.right - chartArea.left, 
            chartArea.bottom - chartArea.top
        );
    }
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    annotationPlugin,
    thresholdBackgroundPlugin
);

// Updated getBarColor function to always return light gray
const getBarColor = (value, thresholds, zeroValue = false) => {
    return 'rgba(200, 200, 200, 0.7)'; // Light gray with some transparency
};

const ModularTrendsChartAlerts = ({ 
    title,
    bedExitAlerts,
    respAlerts,
    visitCountAlerts,
    thresholds
}) => {
    // State for alert type (bedExit, respiratory, visitCount)
    const [alertType, setAlertType] = useState('visitCount');
    
    // Format date for x-axis labels
    const formatDateLabel = (dateString) => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.warn('Invalid date:', dateString);
                return '';
            }
            
            const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
            const monthDay = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
            
            return `${weekday}\n${monthDay}`;
        } catch (error) {
            console.error('Date parsing error:', error);
            return '';
        }
    };

    // Select appropriate data and thresholds based on alert type
    const getActiveData = () => {
        switch (alertType) {
            case 'bedExit':
                return bedExitAlerts || [];
            case 'respiratory':
                return respAlerts || [];
            case 'visitCount':
                return visitCountAlerts || [];
            default:
                return bedExitAlerts || [];
        }
    };

    const getActiveThresholds = () => {
        switch (alertType) {
            case 'bedExit':
                return thresholds.bedExit || thresholds;
            case 'respiratory':
                return thresholds.respiratory || thresholds;
            case 'visitCount':
                return thresholds.visitCount || thresholds;
            default:
                return thresholds;
        }
    };

    const getActiveMetricLabel = () => {
        switch (alertType) {
            case 'bedExit':
                return 'Bed Exit Alerts';
            case 'respiratory':
                return 'Respiratory Alerts';
            case 'visitCount':
                return 'Visit Count Alerts';
            default:
                return 'Alerts';
        }
    };


    // Process data for chart
    const processedData = React.useMemo(() => {
        const data = getActiveData();
        if (!Array.isArray(data) || data.length === 0) {
            // Return empty arrays with reasonable defaults
            return { 
                values: [0], 
                dates: ['No Data'],
                hasData: false 
            };
        }

        // Process data and then reverse both arrays to show oldest to newest
        const processed = data.reduce((acc, item) => {
            // Fix: Ensure consistent handling of all alert types by properly handling both formats
            let value = 0;
            let date = '';
            
            if (typeof item === 'object') {
                // For object format like {value: 5, period_start: '2023-01-01'}
                value = parseFloat(item.value);
                date = item.period_start || '';
            } else {
                // For direct numeric values
                value = parseFloat(item);
            }
            
            acc.values.push(isNaN(value) ? 0 : value);
            acc.dates.push(date ? formatDateLabel(date) : '');

            return acc;
        }, { values: [], dates: [] });

        // Check if we have any non-zero values
        const hasData = processed.values.some(value => value > 0);

        // Add a zero value and placeholder if we have no data
        if (!hasData && processed.values.length === 0) {
            processed.values.push(0);
            processed.dates.push('No Data');
        }

        // Reverse both arrays to show oldest to newest (left to right)
        return {
            values: processed.values.reverse(),
            dates: processed.dates.reverse(),
            hasData: hasData
        };
    }, [alertType, bedExitAlerts, respAlerts, visitCountAlerts]);

    // Determine if a value is zero for coloring purposes
    const zeroValues = processedData.values.map(value => value === 0);
    
    // Get the active thresholds
    const activeThresholds = getActiveThresholds();
    
    // Ensure all threshold values are numbers
    const validThresholds = {
        low: Math.max(0, parseFloat(activeThresholds.low) || 0),
        normalLow: Math.max(0, parseFloat(activeThresholds.normalLow) || 0),
        average: Math.max(0, parseFloat(activeThresholds.average) || 0),
        normalHigh: Math.max(0, parseFloat(activeThresholds.normalHigh)),
        high: Math.max(0, parseFloat(activeThresholds.high)),
        status: activeThresholds.status || 'Unknown'
    };

    // Calculate full range for thresholds to ensure all zones are visible
    const getFullThresholdRange = () => {
        const stdDev = validThresholds.high - validThresholds.normalHigh;
        
        // Find actual min and max values in the data
        const maxDataValue = Math.max(...processedData.values);
        const minDataValue = Math.min(...processedData.values);
        
        // Calculate threshold-based min and max
        let maxThreshold = validThresholds.high + stdDev;
        let minThreshold = Math.max(0, validThresholds.low - stdDev); // Subtract stdDev from low, but keep >= 0
        
        // Adjust if data exceeds current thresholds (add buffer of 2-3 units)
        if (maxDataValue > maxThreshold) {
            maxThreshold = maxDataValue + 1;
        }
        
        if (minDataValue < minThreshold) {
            minThreshold = Math.max(0, minDataValue - 2); // Keep min at least 0
        }
        
        return {
            min: minThreshold,
            max: maxThreshold
        };
    };

    const thresholdRange = getFullThresholdRange();
    
    // Use threshold range for chart min/max
    const effectiveMinValue = thresholdRange.min;
    const effectiveMaxValue = thresholdRange.max;

    // Calculate gradient stops based on full range
    const getPercentage = (value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 0;
        const range = effectiveMaxValue - effectiveMinValue;
        // Invert the percentage calculation to match chart orientation
        return Math.min(Math.max((100 - ((numValue - effectiveMinValue) / range * 100)), 0), 100).toFixed(1);
    };

    // Gradient goes from bottom to top to match chart orientation
    const gradientBackground = `linear-gradient(to top, 
        #FF8A8A 0% ${100 - getPercentage(validThresholds.low)}%, 
        #FFE566 ${100 - getPercentage(validThresholds.low)}% ${100 - getPercentage(validThresholds.normalLow)}%, 
        #90FF8C ${100 - getPercentage(validThresholds.normalLow)}% ${100 - getPercentage(validThresholds.normalHigh)}%, 
        #FFE566 ${100 - getPercentage(validThresholds.normalHigh)}% ${100 - getPercentage(validThresholds.high)}%, 
        #FF8A8A ${100 - getPercentage(validThresholds.high)}% 100%)`;

    // Format value display based on alert type
    const formatDuration = (value) => {
        if (value === 0 || value === null) return '';
        return Math.round(value).toString();
    };

    // Pass zeroValues to getBarColor function
    const barColors = processedData.values.map((value, index) => 
        getBarColor(value, validThresholds, zeroValues[index])
    );

    const calculateDailyStatuses = () => {
        const activeData = getActiveData();
        
        // Check if we have valid data to process
        if (!activeData || !Array.isArray(activeData) || activeData.length === 0) {
            return ['No Data'];
        }

        const statusList = activeData.map(data => {
            // Handle null/undefined values or missing value property
            if (!data || data.value === null || data.value === undefined) {
                return 'No Data';
            }

            // Parse the value if it's a sleep time format (e.g., "8h 30m")
            let value = data.value;
            if (typeof value === 'string' && value.match(/^\d{1,2}h\s\d{1,2}m$/)) {
                value = parseTimeToMinutes(value);
            }

            // Evaluate against thresholds to determine status
            if (value <= validThresholds.low) return 'Caution';
            if (value <= validThresholds.normalLow) return 'Attention';
            if (value <= validThresholds.normalHigh) return 'Good';
            if (value <= validThresholds.high) return 'Attention';
            return 'Caution';
        });
        return statusList;
    }
    
    const statusList = calculateDailyStatuses();
    
    const calculateWeeklyStatus = (statusList) => {
        // If we have no status list or it's empty, return 'No Data'
        if (!statusList || statusList.length === 0) {
            return 'No Data';
        }
        
        // If there is not enough data, return 'Insufficient data'
        const noDataCount = statusList.filter(status => status === 'No Data').length;
        if (noDataCount >= 3 || statusList.length < 3) return 'Insufficient data';

        // Calculate weekly status based on the most common status of the week
        const weeklyStatusCounts = statusList.reduce((acc, status) => {
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
        
        // If all counts are equal, prioritize in this order: Good > Attention > Caution
        const statusPriority = { 'Good': 3, 'Attention': 2, 'Caution': 1, 'No Data': 0 };
        
        const mostCommonStatus = Object.keys(weeklyStatusCounts).reduce((a, b) => {
            // If counts are the same, choose the one with higher priority
            if (weeklyStatusCounts[a] === weeklyStatusCounts[b]) {
                return statusPriority[a] > statusPriority[b] ? a : b;
            }
            // Otherwise choose the one with higher count
            return weeklyStatusCounts[a] > weeklyStatusCounts[b] ? a : b;
        });
        
        return mostCommonStatus;
    };
    
    const weeklyStatus = calculateWeeklyStatus(statusList);


    const data = {
        labels: processedData.dates,
        datasets: [
            {
                type: "line",
                label: "Trend",
                data: processedData.values,
                borderColor: "#3498db",
                borderWidth: 4,
                tension: 0.4,
                fill: false,
                pointBackgroundColor: "#3498db",
                pointRadius: 4,
                order: 1,
                datalabels: {
                    display: false
                },
                // Custom legend styling for line
                pointStyle: 'line',
                pointBorderWidth: 2
            },
            {
                label: getActiveMetricLabel(),
                data: processedData.values,
                type: "bar",
                backgroundColor: 'rgba(200, 200, 200, 0.7)',
                borderColor: "#4B4B4D",      // Light grey
                borderWidth: 1,
                order: 2,
                // Legend styling only (won't affect the actual bars)
                legendPointStyle: 'rect',
                legendBorderColor: 'black',
                legendBackgroundColor: 'transparent'
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 0,
                top: 30,
                right: 20,
                bottom: 10
            }
        },
        scales: {
            y: {
                min: effectiveMinValue,
                max: processedData.hasData ? effectiveMaxValue : 5, // Default max of 5 when no data
                grid: {
                    display: true
                },
                title: {
                    display: true,
                    text: getActiveMetricLabel() + ' (count)',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    // Provide step size to avoid too many ticks
                    stepSize: Math.ceil((effectiveMaxValue - effectiveMinValue) / 10),
                    includeBounds: true,
                    callback: function(value) {
                        // Add threshold labels with proper formatting
                        const thresholdValue = [
                            validThresholds.low,
                            validThresholds.normalLow,
                            validThresholds.normalHigh,
                            validThresholds.high
                        ];
                        
                        // For threshold values, add a small indicator
                        if (thresholdValue.includes(value)) {
                            return `→ ${formatDuration(Math.max(0, value))}`;
                        }
                        
                        return formatDuration(Math.max(0, value));
                    }
                },
                afterTickToLabelConversion: function(axis) {
                    // Ensure we only have unique labels
                    const seen = new Set();
                    axis.ticks = axis.ticks.filter(tick => {
                        const label = tick.label;
                        if (seen.has(label)) {
                            return false;
                        }
                        seen.add(label);
                        return true;
                    });
                    return axis;
                },
                beginAtZero: true
            },
            x: {
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Daily Reports',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    font: {
                        size: 11
                    },
                    padding: 10
                }
            }
        },
        plugins: {
            datalabels: {
                display: true,
                color: 'black',
                anchor: 'end',
                align: 'top',
                offset: -2,
                formatter: function(value) {
                    return formatDuration(Math.max(0, value));
                },
                font: {
                    weight: 'bold',
                    size: 11
                }
            },
            legend: {
               display: true,
                position: 'top',
                align: 'center',
                onHover: (event, legendItem, legend) => {
                    const tooltips = {
                        'Respiratory Alerts': 'Number of respiratory rate alerts',
                        'Bed Exit Alerts': 'Number of bed exit alerts',
                        'Visit Count Alerts': 'Number of visit count alerts',
                        'Alert Threshold': 'Alert threshold level for the facility',
                        'Trend': 'Trend of the metric'
                    };
                    
                    // Change cursor to indicate interactivity
                    event.native.target.style.cursor = 'pointer';
                    
                    // Find tooltip container or create it if it doesn't exist
                    let tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chart-legend-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = 'rgba(0,0,0,0.7)';
                        tooltipEl.style.color = 'white';
                        tooltipEl.style.padding = '5px 10px';
                        tooltipEl.style.borderRadius = '3px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.transition = 'opacity 0.2s';
                        tooltipEl.style.zIndex = '10';
                        document.body.appendChild(tooltipEl);
                    }
                    
                    // Show tooltip with description
                    if (tooltips[legendItem.text]) {
                        tooltipEl.innerHTML = tooltips[legendItem.text];
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = event.native.pageX + 'px';
                        tooltipEl.style.top = (event.native.pageY - 28) + 'px'; // Position above the cursor
                    }
                },
                onLeave: (event, legendItem, legend) => {
                    // Reset cursor
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = 'default';
                    }
                    
                    // Hide tooltip
                    const tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (tooltipEl) {
                        tooltipEl.style.opacity = '0';
                    }
                },
                labels: {
                    boxWidth: 30,
                    padding: 15,
                    usePointStyle: true,
                    pointStyle: true,
                    font: {
                        size: 12,
                        weight: '500'
                    },
                    color: '#444',
                    generateLabels: function(chart) {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => ({
                            text: dataset.label,
                            fillStyle: i === 0 ? dataset.borderColor : 'transparent',
                            strokeStyle: i === 0 ? dataset.borderColor : 'black',
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                            // Use custom legend style for bars, but default for line
                            pointStyle: i === 0 ? dataset.pointStyle : dataset.legendPointStyle,
                            // Use custom colors for legend only
                            borderColor: i === 0 ? dataset.borderColor : dataset.legendBorderColor,
                            backgroundColor: i === 0 ? dataset.borderColor : dataset.legendBackgroundColor
                        }));
                    }
                }
            },
            title: {
                display: true,
                text: [title || getActiveMetricLabel()],
                font: {
                    size: 16,
                    weight: 'bold'
                }
            },
            thresholds: validThresholds,
            annotation: {
                annotations: {
                    // lowLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.low,
                    //     yMax: validThresholds.low,
                    //     borderColor: '#FF8A8A', // Red
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // normalLowLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.normalLow,
                    //     yMax: validThresholds.normalLow,
                    //     borderColor: '#FFE566', // Yellow
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // normalHighLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.normalHigh,
                    //     yMax: validThresholds.normalHigh,
                    //     borderColor: '#FFE566', // Yellow
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // },
                    // highLine: {
                    //     type: 'line',
                    //     yMin: validThresholds.high,
                    //     yMax: validThresholds.high,
                    //     borderColor: '#FF8A8A', // Red
                    //     borderWidth: 2,
                    //     borderDash: [5, 5],
                    //     drawTime: 'beforeDatasetsDraw', // Move behind everything
                    //     label: {
                    //         display: false
                    //     }
                    // }
                }
            },
            thresholdBackground: true,
        }
    };
    // console.log('Thresholds:', validThresholds);
    // console.log('Processed Data:', processedData);
    const chartKey = `alert-chart-${alertType}`;
    return (
        <div style={{ position: 'relative', height: '600px', width: '100%', display: 'flex', flexDirection: 'column', marginTop: '30px', marginBottom: '30px' }}>
            {/* Alert Type Selector */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                <button
                    onClick={() => setAlertType('visitCount')}
                    style={{
                        padding: '5px 15px',
                        backgroundColor: alertType === 'visitCount' ? '#4a90e2' : '#f0f0f0',
                        color: alertType === 'visitCount' ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Visit Alert Count
                </button>
                <button
                    onClick={() => setAlertType('bedExit')}
                    style={{
                        padding: '5px 15px',
                        backgroundColor: alertType === 'bedExit' ? '#4a90e2' : '#f0f0f0',
                        color: alertType === 'bedExit' ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '4px',
                        marginRight: '5px',
                        marginLeft: '5px',
                        cursor: 'pointer'
                    }}
                >
                    Bed Exit Alert
                </button>
                <button
                    onClick={() => setAlertType('respiratory')}
                    style={{
                        padding: '5px 15px',
                        backgroundColor: alertType === 'respiratory' ? '#4a90e2' : '#f0f0f0',
                        color: alertType === 'respiratory' ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '4px',
                        marginRight: '5px',
                        cursor: 'pointer'
                    }}
                >
                    Respiratory Alert
                </button>
            </div>
            
            {/* Chart Container */}
            <div style={{ height: '100%', width: '100%', position: 'relative' }}>
                <Chart 
                    key={chartKey}
                    data={data} 
                    options={{
                        ...options,
                        plugins: {
                            ...options.plugins,
                            title: {
                                ...options.plugins.title,
                                text: [
                                    title || getActiveMetricLabel()
                                ]
                            }
                        }
                    }} 
                    plugins={[
                        {
                            ...thresholdBackgroundPlugin,
                            // Override beforeDraw to directly use validThresholds
                            beforeDraw: (chart) => {
                                // Skip background drawing if no data
                                if (!processedData.hasData) return;
                                
                                const { ctx, chartArea, scales } = chart;
                                if (!chartArea) return;
                                
                                const { top, bottom, left, right, width } = chartArea;
                                const { y } = scales;
                                
                                if (!y) return;
                                
                                // Directly use validThresholds instead of trying to get from chart options
                                const thresholds = getActiveThresholds();
                                
                                // Very Low Zone (Red) - from 0 to low
                                const lowY = y.getPixelForValue(thresholds.low);
                                ctx.fillStyle = 'rgba(255, 138, 138, 0.3)'; // Red with 30% opacity
                                ctx.fillRect(left, lowY, width, bottom - lowY);
                                
                                // Low Zone (Yellow) - from low to normalLow
                                const normalLowY = y.getPixelForValue(thresholds.normalLow);
                                ctx.fillStyle = 'rgba(255, 229, 102, 0.3)'; // Yellow with 30% opacity
                                ctx.fillRect(left, normalLowY, width, lowY - normalLowY);
                                
                                // Normal Zone (Green) - from normalLow to normalHigh
                                const normalHighY = y.getPixelForValue(thresholds.normalHigh);
                                ctx.fillStyle = 'rgba(144, 255, 140, 0.8)'; // Green with 80% opacity
                                ctx.fillRect(left, normalHighY, width, normalLowY - normalHighY);
                                
                                // High Zone (Yellow) - from normalHigh to high
                                const highY = y.getPixelForValue(thresholds.high);
                                ctx.fillStyle = 'rgba(255, 229, 102, 0.3)'; // Yellow with 30% opacity
                                ctx.fillRect(left, highY, width, normalHighY - highY);
                                
                                // Very High Zone (Red) - from high to max
                                ctx.fillStyle = 'rgba(255, 138, 138, 0.3)'; // Red with 30% opacity
                                ctx.fillRect(left, top, width, highY - top);

                                // Fill with white background
                                ctx.fillStyle = 'white';
                                ctx.fillRect(left, top, width, bottom - top);
                            }
                        }
                    ]}
                />
                
                {/* No data overlay */}
                {!processedData.hasData && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        padding: '15px 30px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#666',
                        zIndex: 10
                    }}>
                        No {getActiveMetricLabel()} Data Available
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModularTrendsChartAlerts;
